import { useState } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Phone, Star, ExternalLink, Building2, Waves, Heart, ShieldCheck, Bed, Filter } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      <Star size={12} className="text-gold fill-gold" />
      <span className="text-white/70 text-xs font-body">{rating?.toFixed(1)}</span>
    </div>
  );
}

function PlaceCard({ place, index, t }) {
  const name = typeof place.displayName === "object" ? place.displayName?.text : place.displayName || "Unknown";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -3 }}
      className="glass-card rounded-2xl p-5 hover:border-saffron/20 transition-all duration-300"
      data-testid={`place-card-${place.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-heading font-semibold text-white text-base leading-tight">{name}</h3>
        {place.rating && <StarRating rating={place.rating} />}
      </div>
      {place.formattedAddress && (
        <div className="flex items-start gap-2 mb-2">
          <MapPin size={13} className="text-saffron mt-0.5 shrink-0" />
          <p className="text-white/50 text-xs font-body leading-relaxed">{place.formattedAddress}</p>
        </div>
      )}
      {place.nationalPhoneNumber && (
        <div className="flex items-center gap-2 mb-3">
          <Phone size={13} className="text-saffron shrink-0" />
          <a href={`tel:${place.nationalPhoneNumber}`} className="text-white/60 text-xs font-body hover:text-saffron transition-colors">
            {place.nationalPhoneNumber}
          </a>
        </div>
      )}
      {place.userRatingCount && (
        <p className="text-white/30 text-xs font-body mb-3">{place.userRatingCount.toLocaleString()} {t.loc.reviews}</p>
      )}
      {place.googleMapsUri && (
        <a href={place.googleMapsUri} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-saffron/80 hover:text-saffron font-body transition-colors"
          data-testid={`place-maps-link-${place.id}`}>
          <ExternalLink size={12} /> {t.loc.maps}
        </a>
      )}
    </motion.div>
  );
}

export default function Locations() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [isMock, setIsMock] = useState(false);

  const CATEGORIES = [
    { key: "all", label: t.loc.all, icon: Filter },
    { key: "temple", label: t.loc.temples, icon: Building2, query: "temple ujjain" },
    { key: "ghat", label: t.loc.ghats, icon: Waves, query: "ghat kshipra ujjain" },
    { key: "hospital", label: t.loc.hospitals, icon: Heart, query: "hospital ujjain" },
    { key: "police", label: t.loc.police, icon: ShieldCheck, query: "police station ujjain" },
    { key: "hotel", label: t.loc.hotels, icon: Bed, query: "hotel ujjain" },
  ];

  const handleSearch = async (cat = activeCategory, q = query) => {
    const finalQuery = q.trim() || (CATEGORIES.find(c => c.key === cat)?.query) || "ujjain";
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.post(`${API}/places/search`, {
        q: finalQuery,
        category: cat === "all" ? null : cat,
        page_size: 12,
      });
      setPlaces(res.data.places || []);
      setIsMock(res.data.mock || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    const defQuery = cat === "all" ? "places near ujjain" : CATEGORIES.find(c => c.key === cat)?.query || cat;
    setQuery("");
    handleSearch(cat, defQuery);
  };

  return (
    <div className="min-h-screen bg-stone-900 pt-28 pb-20" data-testid="locations-page">
      {/* Hero */}
      <div className="relative overflow-hidden mb-12">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1761645749643-1f44c6684558?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt="Ghats" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/80 to-stone-900" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">{t.loc.badge}</p>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">{t.loc.title}</h1>
            <p className="font-body text-white/50 text-base mb-8">{t.loc.subtitle}</p>
          </motion.div>

          {/* Search Box */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="flex gap-3 max-w-xl mx-auto">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                placeholder={t.loc.placeholder}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white placeholder:text-white/30 text-sm font-body outline-none transition-all"
                data-testid="location-search-input"
              />
            </div>
            <button onClick={() => handleSearch()} disabled={loading}
              className="px-6 py-3 rounded-xl bg-saffron text-stone-900 font-body font-semibold text-sm hover:bg-saffron-light transition-all active:scale-95 disabled:opacity-60"
              data-testid="location-search-btn">
              {loading ? "..." : t.loc.btn}
            </button>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {CATEGORIES.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => handleCategoryClick(key)}
              data-testid={`category-btn-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200 ${
                activeCategory === key
                  ? "bg-saffron text-stone-900 shadow-[0_0_15px_rgba(255,153,51,0.3)]"
                  : "bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20"
              }`}>
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Mock notice */}
        {isMock && (
          <div className="mb-6 px-4 py-2.5 rounded-lg bg-gold/10 border border-gold/20 text-gold/80 text-xs font-body">
            {t.loc.demo}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="glass-card rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-white/10 rounded mb-3 w-3/4" />
                <div className="h-3 bg-white/5 rounded mb-2 w-full" />
                <div className="h-3 bg-white/5 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        {!loading && searched && places.length > 0 && (
          <div>
            <p className="text-white/40 text-sm font-body mb-5">{places.length} {t.loc.found}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="places-grid">
              {places.map((place, i) => <PlaceCard key={place.id} place={place} index={i} t={t} />)}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && searched && places.length === 0 && (
          <div className="text-center py-20">
            <MapPin size={40} className="text-white/20 mx-auto mb-4" />
            <p className="font-heading text-white/40 text-xl">{t.loc.empty}</p>
            <p className="font-body text-white/30 text-sm mt-2">{t.loc.emptyHint}</p>
          </div>
        )}

        {/* Prompt to search */}
        {!searched && !loading && (
          <div className="text-center py-20">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              <div className="w-20 h-20 rounded-full bg-saffron/10 flex items-center justify-center mx-auto mb-6 animate-float">
                <MapPin size={32} className="text-saffron" />
              </div>
              <p className="font-heading text-white/60 text-xl mb-3">{t.loc.prompt}</p>
              <p className="font-body text-white/30 text-sm">{t.loc.promptHint}</p>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
